import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { hasConstrainedMediaConnection } from './monolyt-media-policy.js?v=2600083df17ccd19bc7a6adc6f5ae417cc3913aaa3119ca87d4e893b97c56623';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  #videoPauseVersion = 0;

  #pendingLoad = null;
  static #productCache = new Map();

  /** One short-lived, shared GET for gallery/zoom; failures never poison the cache. */
  static #getProduct(url, productId) {
    const key = url.href + '|' + productId;
    const now = Date.now();
    for (const [cachedKey, entry] of this.#productCache) {
      if (entry.expiresAt <= now) this.#productCache.delete(cachedKey);
    }
    const cached = this.#productCache.get(key);
    if (cached) return cached.promise;

    const controller = new AbortController();
    const entry = { expiresAt: Infinity, promise: null };
    let timeout;
    const deadline = new Promise((_, reject) => {
      timeout = setTimeout(() => {
        controller.abort();
        reject(new Error('Product video lookup timed out'));
      }, 30000);
    });
    const request = (async () => {
      const response = await fetch(url.href, {
        method: 'GET', credentials: 'same-origin', redirect: 'error',
        headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (!response.ok || response.redirected) throw new Error('Product video lookup failed');
      const product = await response.json();
      if (String(product?.id) !== productId || !Array.isArray(product.media)) {
        throw new Error('Product video identity differs');
      }
      return product;
    })();
    entry.promise = Promise.race([request, deadline]).then((product) => {
      entry.expiresAt = Date.now() + 60000;
      return product;
    }).catch((error) => {
      if (this.#productCache.get(key) === entry) this.#productCache.delete(key);
      throw error;
    }).finally(() => clearTimeout(timeout));
    this.#productCache.set(key, entry);
    return entry.promise;
  }

  /** Select only an existing, playable native MP4 belonging to this exact media. */
  #getSmallestSource(product, mediaId, template) {
    const matches = product.media.filter((item) => String(item.id) === mediaId && item.media_type === 'video');
    if (matches.length !== 1 || !Array.isArray(matches[0].sources)) return null;
    const originalUrls = [template.getAttribute('src'), ...Array.from(template.querySelectorAll('source'), (source) => source.getAttribute('src'))];
    const originalPaths = new Set(originalUrls.map((url) => this.#getNativeVideoUrl(url)?.path).filter(Boolean));
    let originalPixels = Infinity;
    let selected = null;
    for (const source of matches[0].sources) {
      if (source.format !== 'mp4' || source.mime_type !== 'video/mp4'
        || !Number.isFinite(source.width) || source.width <= 0
        || !Number.isFinite(source.height) || source.height <= 0
        || typeof source.url !== 'string') continue;
      try {
        const native = this.#getNativeVideoUrl(source.url);
        const pixels = source.width * source.height;
        if (!native || !Number.isFinite(pixels)) continue;
        if (originalPaths.has(native.path)) originalPixels = Math.min(originalPixels, pixels);
        if (!selected || pixels < selected.pixels) selected = { url: native.url, pixels };
      } catch { /* Malformed source metadata cannot replace the original player. */ }
    }
    // Unknown originals stay untouched; the known original is itself a candidate,
    // so the selected source cannot increase its pixel dimensions.
    return Number.isFinite(originalPixels) && selected && selected.pixels <= originalPixels ? selected.url : null;
  }

  #getNativeVideoUrl(value) {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value, document.baseURI);
      const storefront = new URL(document.baseURI);
      const cdn = url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/videos/');
      const local = url.origin === storefront.origin && url.pathname.startsWith('/cdn/shop/videos/');
      if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash
        || !(cdn || local) || !url.pathname.toLowerCase().endsWith('.mp4')) return null;
      // Shopify's storefront proxy and CDN expose the same native path. Ignore
      // version queries only for identity comparison; use the JSON URL verbatim.
      return { url: url.href, path: local ? url.pathname.slice('/cdn/shop'.length) : url.pathname };
    } catch { return null; }
  }

  #getProductMetadata(template) {
    const { monolytProductJson, monolytProductId, monolytMediaId } = this.dataset;
    if (!monolytProductJson || !/^[1-9]\d*$/.test(monolytProductId || '') || !/^[1-9]\d*$/.test(monolytMediaId || '')) return null;
    if (!(template instanceof HTMLVideoElement) || this.hasAttribute('autoplay')
      || !template.canPlayType('video/mp4')
      || !(navigator.connection?.saveData || hasConstrainedMediaConnection())) return null;
    try {
      // Liquid supplies the localized native route; never derive a handle from the current page URL.
      const url = new URL(monolytProductJson, document.baseURI);
      const root = window.Shopify?.routes?.root;
      if (typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//') || /[?#\\]/.test(root)) return null;
      const prefix = new URL(root.replace(/\/?$/, '/') + 'products/', document.baseURI);
      if (url.protocol !== 'https:' || url.origin !== new URL(document.baseURI).origin
        || url.username || url.password || url.port || url.search || url.hash
        || !url.pathname.startsWith(prefix.pathname) || !url.pathname.endsWith('.js')) return null;
      const handle = decodeURIComponent(url.pathname.slice(prefix.pathname.length, -3));
      if (!handle || /[/\\?#\u0000]/.test(handle)) return null;
      return { url, productId: monolytProductId, mediaId: monolytMediaId };
    } catch { return null; }
  }

  #loadProductVideo(template, metadata, focus) {
    const pending = {};
    this.#pendingLoad = pending;
    this.refs.deferredMediaPlayButton?.setAttribute('aria-busy', 'true');
    this.dataset.monolytSourceStatus = 'loading';
    pending.promise = (async () => {
      let source = null;
      try {
        const product = await DeferredMedia.#getProduct(metadata.url, metadata.productId);
        source = this.#getSmallestSource(product, metadata.mediaId, template);
        if (!source) DeferredMedia.#productCache.delete(metadata.url.href + '|' + metadata.productId);
      } catch {
        DeferredMedia.#productCache.delete(metadata.url.href + '|' + metadata.productId);
        // Same click continues with the original player after a completed failed lookup.
      }
      if (this.#pendingLoad !== pending || !this.isConnected) return;
      // The inert template is not cloned until the GET has settled. Set src in
      // the same synchronous step as cloning, before insertion/resource selection.
      const content = template.cloneNode(true);
      if (source) content.src = source;
      this.dataset.monolytSourceStatus = source ? 'product-json' : 'native-fallback';
      this.#appendContent(content, focus);
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
    })().catch((error) => console.error(error)).finally(() => {
      if (this.#pendingLoad === pending) {
        this.#pendingLoad = null;
        this.refs.deferredMediaPlayButton?.removeAttribute('aria-busy');
      }
    });
    return pending.promise;
  }

  /** @param {HTMLVideoElement} video */
  #playVideo(video) {
    const pauseVersion = this.#videoPauseVersion;
    video.play()?.catch((error) => {
      // A deliberate pause cancels its pending native play promise. An earlier
      // pause must not excuse a failure from a subsequent play attempt.
      if (error.name === 'AbortError' && this.#videoPauseVersion !== pauseVersion) return;
      if (this.dataset.monolytSourceStatus && this.#videoPauseVersion === pauseVersion) {
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
      }
      console.error(error);
    });
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    this.pauseMedia();
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    const pending = this.loadContent(true);
    if (pending) return;
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;
    if (this.#pendingLoad) return this.#pendingLoad.promise;
    const template = this.querySelector('template')?.content.firstElementChild;
    if (!template) return;
    const metadata = this.#getProductMetadata(template);
    // Dispatch before installing this intent: our own existing pause listener
    // must pause old content without cancelling the new lookup. Other starts cancel it.
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    if (metadata) return this.#loadProductVideo(template, metadata, focus);
    this.#appendContent(template.cloneNode(true), focus);
  }

  #appendContent(content, focus) {
    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      this.#playVideo(content);
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying || this.#pendingLoad) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) this.#playVideo(video);
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    if (this.#pendingLoad) {
      this.#pendingLoad = null;
      this.refs.deferredMediaPlayButton?.removeAttribute('aria-busy');
      this.dataset.monolytSourceStatus = 'cancelled';
    }
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        this.#videoPauseVersion += 1;
        video.pause();
      }
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
